import {
    takeEvery,
    all,
    call,
    put,
    select,
} from 'redux-saga/effects';
import actionFactory from '../../../webcommon/header/filterheader/actions';
import moment from 'moment';
import { push, replace, LOCATION_CHANGE } from 'connected-react-router';
import urlJoin from 'url-join';
import * as filterHeaderSelectors from '../../../webcommon/header/filterheader/selectors';
import fp from 'lodash/fp';
import queryString from 'query-string';
import * as actions from './actions';
import * as selectors from './selectors';
import {
    searchToFilters, filtersToSearch, encodeSortingIntoUrl, urlToSort
} from '../../../common/urlUtils';
import * as webJobsApi from '../../../api/webJobsApi';
import * as routerSelectors from '../../../common/router/selectors';
import * as appConstants from '../../../common/constants';

import { getVirtualDirectory } from '../../../common/helpermethods/HelperMethods';

const webJobsDataLoadPath = urlJoin(getVirtualDirectory(), '/commodityReport/triggerJobs');
const webJobsDetailsPath = urlJoin(getVirtualDirectory(), '/commodityReport/triggerJobs/details');
const filterHeaderActions = actionFactory('CommodityJobs');

export function* filterChanged(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        const currentlyOpenFilter = yield (select(filterHeaderSelectors.getCurrentlyOpenFilter, 'CommodityJobs'));
        if (!currentlyOpenFilter) {
            const filters = yield select(filterHeaderSelectors.getFilters, 'CommodityJobs');
            const gridFiltering = yield select(selectors.getDefaultSort);
            const filterSearch = filtersToSearch(filters);
            const gridSearch = encodeSortingIntoUrl(gridFiltering);
            const search = `${filterSearch}&${gridSearch}`;
            const sp = queryString.parse(search);
            let force = false;
            if (action && (
                action.type === actions.WEB_JOBS_LOADED || action.type === actions.REFRESH_DATA
            )) {
                sp.force = true;
                force = true;
            }
            const newSearch = `?${queryString.stringify(sp)}`;
            const currentSearch = yield select(routerSelectors.getSearch);
            if (force || !currentSearch) {
                yield put(replace(`${webJobsDataLoadPath}${newSearch}`));
            } else if (currentSearch !== newSearch) {
                yield put(push(`${webJobsDataLoadPath}${newSearch}`));
            }
        }
    }
}

export function* webJobsLoaded(action) {
    try {
        const initialised = yield (select(selectors.getInitialised));
        if (!initialised) {
            yield put(actions.setLoading(true));
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            yield put(filterHeaderActions.setFilterDefinitions(filterDefinitions));
            const defaultFilters = yield select(selectors.getDefaultFilters);
            yield put(filterHeaderActions.setDefaultFilters(defaultFilters));
            if (action.search === '') {
                yield put(filterHeaderActions.resetFilters());
            } else {
                const filters = searchToFilters(action.search, filterDefinitions, defaultFilters);
                yield put(actions.setAllGridSorts(urlToSort(action.search)));
                yield put(filterHeaderActions.setFilters(filters));
            }
            yield put(actions.setInitialised(true));
            yield call(filterChanged, action);
        }
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* goToDetails(action) {
    const search = `id=${encodeURIComponent(JSON.stringify((action.details)))}`;
    const sp = queryString.parse(search);
    const newSearch = `?${queryString.stringify(sp)}`;
    yield put(push(`${webJobsDetailsPath}${newSearch}`));
}

export function* newLoadRequestOpened(api) {
    try {
        const previousLoadRequestMappings = yield select(selectors.getLoadRequestMappings);
        if (previousLoadRequestMappings.length === 0) {
            const loadRequestMappings = yield call(api.loadRequestMappings);
            yield put(actions.setLoadRequestMappings(loadRequestMappings));
        }
    } catch (error) {
        yield put(actions.fetchLoadRequestOptionsFailed('Failed to fetch load request options'));
    }
}

export function* createNewLoadRequest(api, action) {
    try {
        const requestObject = {
            dataSource: 'CommodityFutureReport',
            category: action.category,
            businessDate: moment(action.date).format(appConstants.dateFormat),
            endDate: action.endDate !== '' ? moment(action.endDate).format(appConstants.dateFormat) : null,
            runSecSegregatedMode: true
        };
        yield call(api.createNewLoadRequest, requestObject);
    } catch (error) {
        yield put(actions.createLoadRequestFailed('Failed to create a new load request'));
    }
}

export function* refreshWebJobsData(api) {
    try {
        const filters = yield select(filterHeaderSelectors.getFilters, 'CommodityJobs');
        const requestedServerFilters = fp.flow(
            fp.filter(filter => filter.value && filter.definition.server),
            fp.sortBy(filter => filter.definition.id),
            JSON.stringify
        )(filters);
        yield put(actions.setLoading(true));
        const data = yield call(api.fetchDataLoads, filters);
        const filteredData = data.filter( datum => datum.source === 'CommodityFutureReport')
        yield put(actions.setRows(filteredData, requestedServerFilters));
    } catch (error) {
        yield put(actions.fetchDataLoadsFailed(error.message));
    } finally {
        yield put(actions.setLoading(false));
    }
}


export function* handleLocationChanged(api, action) {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        try {
            if (action.payload.location.search === '') {
                // redirect to the page without query params,
                // probably from the navbar, so need to rebuild the url
                yield call(filterChanged);
            } else {
                const filterDefinitions = yield select(selectors.getFilterDefinitions);
                const defaultFilters = yield select(selectors.getDefaultFilters);
                const filters = searchToFilters(action.payload.location.search,
                    filterDefinitions, defaultFilters);
                yield put(actions.setAllGridSorts(urlToSort(action.payload.location.search)));
                yield put(filterHeaderActions.setFilters(filters));
                const params = queryString.parse(action.payload.location.search);
                const force = params.force === 'true';

                const requestedServerFilters = fp.flow(
                    fp.filter(filter => filter.value && filter.definition.server),
                    fp.sortBy(filter => filter.definition.id),
                    JSON.stringify
                )(filters);
                const currentServerFilters = yield select(selectors.getCurrentServerFilters);
                if (force || (currentServerFilters !== requestedServerFilters)) {
                    yield put(actions.setLoading(true));
                    const data = yield call(api.fetchDataLoads, filters);
                    const filteredData = data.filter( datum => datum.source === 'CommodityFutureReport')
                    yield put(actions.setRows(filteredData, requestedServerFilters));
                }
            }
        } catch (error) {
            yield put(actions.fetchDataLoadsFailed(error.message));
        } finally {
            yield put(actions.setLoading(false));
        }
    }
}

export function* rerunSelectedRows(api, action) {
    try {
        yield call(api.rerunSelectedRows, action.rows);
    } catch (error) {
        yield put(actions.rerunSelectedRowsFailed('Failed to rerun selected rows'));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(actions.WEB_JOBS_LOADED, webJobsLoaded),
        takeEvery(actions.LOAD_REQUEST_OPENED, newLoadRequestOpened, webJobsApi),
        takeEvery(actions.CREATE_LOAD_REQUEST, createNewLoadRequest, webJobsApi),
        takeEvery(actions.SET_DETAILS_BEING_VEIWED, goToDetails),
        takeEvery(actions.RERUN_SELECTED_ROWS, rerunSelectedRows, webJobsApi),
        takeEvery([
            filterHeaderActions.UPDATE_FILTER,
            filterHeaderActions.REMOVE_FILTER,
            filterHeaderActions.ADD_FILTER,
            filterHeaderActions.RESET_FILTERS,
            actions.REFRESH_DATA
        ], filterChanged),
        takeEvery(
            action => action.type === LOCATION_CHANGE
            && action.payload.location.pathname.toLowerCase() === webJobsDataLoadPath.toLowerCase(),
            handleLocationChanged,
            webJobsApi
        ),
        takeEvery(actions.SET_GRID_SORTS, filterChanged, false),
        takeEvery(actions.REFRESH_WEB_JOBS_DATA_LOADS, refreshWebJobsData, webJobsApi)
    ]);
}
